<template>
    <div class="content-wrapper">
        <!-- Page Loader -->
        <div class="vld-parent">
            <loading :active.sync="isLoading" :is-full-page="fullPage"></loading>
        </div>

        <breadcrumb :page="$t('form.invoice.studentPendingInvoices')"></breadcrumb>

        <div class="content">
            <div class="container-fluid">

                <b-alert v-model="alert" :variant="alertClass" dismissible fade>{{alertMessage}}</b-alert>
                <div class="card">
                    <div class="card-header bg-light">
                        <div class="card-title">{{$t('filters')}}</div>
                        <div class="text-right">
                            <b-button variant="primary" size="sm" @click="detailedSearch = !detailedSearch">
                                {{$t('button.detailedSearch')}}</b-button>
                            <b-button variant="default" size="sm" @click="clearSearch()">{{$t('button.resetFilter')}}
                            </b-button>
                        </div>
                    </div>
                    <div class="card-body">

                        <div class="row">
                            <div class="col-sm-6">
                                <div class="form-group">
                                    <label>{{$t('form.invoice.searchCriteria')}}</label>
                                    <v-select @input="loadPreset" label="name" :options="presetList" v-model="preset">
                                    </v-select>
                                </div>
                            </div>
                        </div>

                        <div class="row">
                            <div class="col-sm-3">
                                <div class="form-group">
                                    <label>{{$t('form.invoice.invoiceNumber')}}</label>
                                    <input type="text" v-model="search.number" @keyup="searchInvoice"
                                        class="form-control">
                                </div>
                            </div>

                            <div class="col-sm-3">
                                <div class="form-group">
                                    <label>{{$t('form.invoice.studentNo')}}</label>
                                    <input type="number" @keyup="searchInvoice" v-model="search.studentNumber" class="form-control">
                                </div>
                            </div>

                            <div class="col-sm-3">
                                <div class="form-group">
                                    <label>{{$t('form.invoice.status')}}</label>
                                    <v-select @input="searchInvoice" v-model="search.status" :options="statusArray">
                                    </v-select>
                                </div>
                            </div>

                            <div class="col-sm-3">
                                <div class="form-group">
                                    <label>{{$t('form.invoice.invoiceDate')}}</label>
                                    <date-picker :lang="lang" class="date-picker" v-model="search.invoiceDate" valueType="format"
                                        :format="dateFormat" @change="searchInvoice"
                                        :popup-style="{ top: '100%', left: 0}" :append-to-body="false">
                                    </date-picker>
                                </div>
                            </div>

                            <div class="col-sm-3">
                                <div class="form-group">
                                    <label>{{$t('form.invoice.dueDate')}}</label>
                                    <date-picker :lang="lang" class="date-picker" v-model="search.dueDate" valueType="format"
                                        :format="dateFormat" @change="searchInvoice"
                                        :popup-style="{ top: '100%', left: 0}" :append-to-body="false">
                                    </date-picker>
                                </div>
                            </div>
                        </div>

                        <div class="row" v-if="detailedSearch">
                            <div class="col-sm-3">
                                <div class="form-group">
                                    <label>{{$t('form.invoice.amount')}}</label>
                                    <input type="text" v-model="search.amount" @keyup="searchInvoice"
                                        class="form-control">
                                </div>
                            </div>
                            
                            <div class="col-sm-3">
                                <div class="form-group">
                                    <label>{{$t('form.invoice.currency')}}</label>
                                    <v-select @input="searchInvoice" :options="currencies" v-model="search.currency"
                                        :reduce="currencies => currencies"></v-select>
                                </div>
                            </div>

                            <div class="col-sm-3">
                                <div class="form-group">
                                    <label>{{$t('form.invoice.invoicePurpose')}}</label>
                                    <v-select @input="searchInvoice" :options="invoicePurposes"
                                        v-model="search.invoicePurpose" :reduce="invoicePurposes => invoicePurposes">
                                    </v-select>
                                </div>
                            </div>


                            <div class="col-12">
                                <hr>
                            </div>

                            <div class="col-sm-6">
                                <div class="form-group">
                                    <input type="text" v-model="preset.name" class="form-control"
                                        :placeholder="$t('form.book.presetName')">
                                </div>
                            </div>

                            <div class="col-md-6">
                                <button class="btn btn-primary" @click="savePreset">{{$t('button.save')}}</button>
                                <button class="btn btn-danger" v-show="presetSelected"
                                    @click="deletePreset">{{$t('button.delete')}}</button>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- List -->
                <div class="card">
                    <div class="card-header bg-light">
                        <div class="card-title">{{$t('form.invoice.studentPendingInvoices')}}</div>
                    </div>
                    <div class="card-body table-responsive p-0">
                        <table class="table table-hover" v-if="invoices.length">
                            <thead>
                                <tr>
                                    <th><input type="checkbox" @change="toggleCheck" v-model="selectedInvoices" class="mt-1 ml-1"></th>
                                    <th>{{$t('form.invoice.number')}}</th>
                                    <th>{{$t('form.invoice.status')}}</th>
                                    <th>{{$t('form.invoice.studentNumber')}}</th>
                                    <th>{{$t('form.invoice.studentName')}}</th>
                                    <th>{{$t('form.invoice.programType')}}</th>
                                    <th>{{$t('form.invoice.date')}}</th>
                                    <th>{{$t('form.invoice.deliveryDate')}}</th>
                                    <th>{{$t('form.invoice.dueDate')}}</th>
                                    <th>{{$t('form.invoice.amount')}}</th>
                                    <th>{{$t('form.invoice.invoicePurpose')}}</th>
                                    <th>{{$t('form.invoice.payment')}}</th>
                                    <th>{{$t('form.invoice.action')}}</th>
                                </tr>
                            </thead>
                            <tbody v-for="(invoice, index) in invoices" v-bind:key="index">
                                <tr @dblclick="goToEdit(invoice.ID)">
                                    <td><input type="checkbox" class="mt-1 ml-1" v-model="selectedInvoices" :value="invoice"></td>
                                    <td>{{invoice.InvoiceNumber}}</td>
                                    <td v-if="invoice.Status==1">{{$t('form.invoice.status_pending')}}</td>
                                    <td v-else-if="invoice.Status==2">{{$t('form.invoice.status_approved')}}</td>
                                    <td v-else-if="invoice.Status==3">{{$t('form.invoice.status_sent')}}</td>
                                    <td v-else-if="invoice.Status==4">{{$t('form.invoice.status_paid')}}</td>
                                    <td v-else-if="invoice.Status==null ||invoice.Status==undefined ||invoice.Status==0"></td>

                                    <td>
                                        <template v-if="invoice.student.length">
                                            <router-link
                                                :to="{name: 'StudentEdit', params: {id: invoice.student[0].StudentNumber}}">
                                                {{invoice.student[0].StudentNumber}}
                                            </router-link>
                                        </template>
                                    </td>
                                    <td>{{invoice.student[0] ? invoice.student[0].FirstName :"" }} {{invoice.student[0] ? invoice.student[0].LastName: ""}}</td>
                                    <td>{{invoice.student[0] ? invoice.student[0].programs[0].ProgramType:""}}</td>
                                    <td>{{invoice.InvoiceDate | formatDate(dateFormat)}}</td>
                                    <td>{{invoice.DeliveryDate | formatDate(datetimeFormat)}}</td>
                                    <td>{{invoice.DueDate | formatDate(dateFormat)}}</td>
                                    <td>
                                        <template v-if="invoice.Discount > 0 && invoice.PaymentPlan != 'Full Payment' && !invoice.acceptanceFee">
                                            {{invoice.Currency}}
                                            {{invoice.DiscountedAmount | curreny(decimalDigits, decimalPoint, thousandSeparator)}}
                                            ({{invoice.Discount}}% {{$t('form.invoice.discount')}})
                                        </template>
                                        <template v-else-if="invoice.Version === 'V1' && invoice.PaymentPlan === 'Full Payment' && !invoice.acceptanceFee">
                                            {{ invoice.Currency }}
                                            {{ invoice.FullPaymentDiscountedAmount | curreny(decimalDigits, decimalPoint, thousandSeparator) }}
                                        </template>
                                        <template v-else-if="invoice.Version === 'V2' && invoice.PaymentPlan === 'Full Payment' && !invoice.acceptanceFee">
                                            {{invoice.Currency}}
                                            {{invoice.Total | curreny(decimalDigits, decimalPoint, thousandSeparator)}}
                                        </template>
                                        <template v-else>
                                            {{invoice.Currency}}
                                            {{invoice.Amount | curreny(decimalDigits, decimalPoint, thousandSeparator)}}
                                        </template>
                                    </td>
                                    <td>{{invoice.InvoicePurpose}}</td>
                                    <td>{{invoice.PaymentPlan | capitalize}}</td>
                                    <td >
                                        <button v-if="permissions.viewInvoices" @click="goToShow(invoice.ID)"
                                            class="btn btn-dark btn-sm">
                                            <i class="fa fa-print"></i>
                                        </button>
                                        <button v-if="permissions.viewInvoicesStudents" @click="goToEdit(invoice.ID)"
                                            class="btn btn-info btn-sm">
                                            <i class="fa fa-edit"></i>
                                        </button>
                                         <button class="btn btn-danger btn btn-sm" v-on:click="deleteInvoice(invoice.ID)"
                                            v-show="permissions.deleteInvoicesStudents">
                                            <span class="fa fa-trash"></span>
                                        </button>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                        <p class="p-3 m-0" v-else>{{$t('noRecord')}}</p>
                    </div>

                    <div class="container-fluid" v-if="invoices.length>0">
                        <button v-if="permissions.createInvoicesStudents" @click="bulkApprove()"
                                class="btn btn-primary btn-sm mb-3" style="letter-spacing: 0.0625em;">
                                {{$t('form.invoice.approveInvoice')}}
                        </button>
                    </div>                    
                    <pagination v-if="pagination.last_page > 1" :pagination="pagination" :offset="5"
                        @paginate="paginate()">
                    </pagination>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    export default {
        props: ['permissions', 'dateFormat', 'datetimeFormat', 'decimalDigits', 'decimalPoint', 'thousandSeparator'],

        data() {
            return {

                alert: false,
                alertClass: '',
                alertMessage: '',

                isLoading: false,
                fullPage: true,
                
                pending:true,

                lang: {
                    formatLocale: {
                        firstDayOfWeek: 1,
                    },
                },
                
                request_source: '',
                detailedSearch: false,
                presetSelected: false,

                pagination: {
                    current_page: 1
                },

                search: {
                    paid: '',
                    invoicePurpose: '',
                    currency: '',
                    status:'',
                    number:'',
                    studentNumber:''
                },

                preset: {
                    name: '',
                    entity: 'studentUnpaidInvoice',
                    value: {}
                },

                invoicePurposes: [],
                currencies: [],
                presetList: [],
                invoices: [],
                selectedInvoices:[],

                statusArray:[],
                status:'',
            }
        },

        async mounted() {
            this.isLoading = true;
            
            if (!this.permissions.viewInvoicesStudents) {
                return this.$router.push('/');
            }

            await this.getInitialData();

            if (localStorage.getItem('filter-sinvoice-pending')) {
                this.search = JSON.parse(localStorage.getItem('filter-sinvoice-pending'));
            }

            this.getAllStatus();
            this.getInvoices();
            this.getPresets();
        },

        watch: {
            search: {
                handler(newSearch) {
                    localStorage.setItem('filter-sinvoice-pending', JSON.stringify(this.search));

                    const emojiRegex = /[\u{10000}-\u{10FFFF}]/gu;
                    
                    // Loop through all properties in the search object
                    Object.keys(newSearch).forEach(key => {
                    // Only process string values
                    if (typeof newSearch[key] === 'string' && emojiRegex.test(newSearch[key])) {
                        // Strip the emoji and update the reactive property
                        this.search[key] = newSearch[key].replace(emojiRegex, '');
                    }
                    });
                },
                deep: true  
            }
        },

        methods: {
            /**
             * Build Query String for Filters
             */
            buildQueryString(obj) {
                let query = '';

                Object.keys(obj).forEach(function (key) {
                    if (obj[key] != '' && obj[key] != null) {
                        query += `${key}=${encodeURIComponent(obj[key])}&`;
                    }
                });

                if (this.pagination && this.pagination.current_page) {
                    query += `page=${this.pagination.current_page}`;
                    return query;
                }

                return query.slice(0, -1);
            },

            searchInvoice() {
                this.pagination.current_page = 1;
                this.getInvoices();
            },

            getAllStatus() {
                let self = this;
                return new Promise(function(resolve, reject) {
                axios.get('/api/config')
                .then(response => {
                if (response.data) {
                    let config = response.data;
                    self.status = config.status;
                    if (( typeof self.status == 'object') && (self.status != '')) {
                            self.statusArray = Object.keys(self.status);
                        }
                    }
                })
                resolve('successfully got config values');
                });
            },
 
            clearSearch() {
                this.search = {
                    paid: '',
                    invoicePurpose: '',
                    currency: '',
                    status: '',
                    number:''
                };

                this.searchInvoice();
            },
            /**
             * Delete invoice
             */
            deleteInvoice(index) {

                if(!this.permissions.deleteInvoicesStudents){
                    this.showAlert(this.$t('form.validation.cantDeleteInvoice'));
                    return;
                }

                let conf = confirm(this.$t('form.validation.delete'));
                
                if (conf === true) {
                    this.isLoading = true;
                    axios
                        .delete("/api/invoices/" + index)
                        .then(response => {
                            this.isLoading = false;
                            this.invoices.splice(index, 1);
                            this.showAlert(response.data.message);
                            this.getInvoices();
                        })
                        .catch(error => {
                            this.isLoading = false;
                            this.showAlert(error.response.data.message,'danger');
                        });
                }
            },

            goToEdit(id) {
                if (!this.permissions.viewInvoicesStudents) return;

                this.$router.push({
                    name: 'StudentInvoiceEdit',
                    params: {
                        id: id
                    }
                });
            },
            
            getDate(date){
                var dateVar  = date?moment(date).format(this.dateFormat):moment().format(this.dateFormat)
                return dateVar;
            },

            toggleCheck(event){
            if (event.target.checked) {
                    this.selectedInvoices=this.invoices
                } else {
                    this.selectedInvoices=[]
                }
            },
            addToSendEmail(invoice){
             if(!(this.selectedInvoices.includes(invoice))){
                this.selectedInvoices.push(invoice)
            }
            },
            // Save a Preset with Unique Name
            savePreset() {
                this.isLoading = true;
                this.alert = false;

                this.preset.value = JSON.stringify(this.search);

                axios
                    .post('/api/presets', this.preset)
                    .then(response => {
                        this.isLoading = false;
                        this.showAlert(response.data.message);
                        this.getPresets();
                    }, error => {
                        this.isLoading = false;

                        if (error.response.status == 422) {
                            this.showAlert(error.response.data.errors.name[0], 'danger');
                        }
                    });
            },

            // Get Presets in Search Criteria 
            getPresets() {
                axios
                    .get('/api/presets/?entity=' + this.preset.entity)
                    .then(response => {
                        this.presetList = response.data;
                    }, error => {
                        console.log(error.response);
                    });
            },

            // Get Search results according to selected Preset Name
            loadPreset() {
                if (!this.preset) {
                    this.preset = {
                        entity: 'studentInvoice'
                    };
                    this.clearSearch();
                    this.alert = false;
                    this.presetSelected = false;
                    return;
                }
                this.alert = false;
                this.presetSelected = true;
                this.search = JSON.parse(this.preset.value);
                this.searchInvoice();
            },

            // Delete a selected Preset
            deletePreset() {
                let conf = confirm(this.$t('form.validation.delete'));

                if (conf === true) {
                    axios
                        .delete("/api/presets/" + this.preset.id)
                        .then(response => {
                            if (response.data.status == 'success') {
                                this.showAlert(response.data.message);
                                this.getPresets();
                                this.clearSearch();
                                this.preset.name = '';
                                this.preset.value = {};
                                this.presetSelected = false;
                            }
                        })
                        .catch(error => {
                            this.isLoading = false;
                        });
                }
            },
            bulkApprove(){
            if(this.selectedInvoices.length>0){
                this.isLoading = true;
                axios
                    .post('/api/students/invoices/bulk-approve',{
                        selectedInvoices:this.selectedInvoices
                    })
                    .then(response => {
                        this.isLoading = false;
                        this.selectedInvoices = []
                        this.getInvoices();

                        let xInvoicesApproved = this.$t('form.invoice.xInvoicesApproved', {
                            X: response.data.data
                        });

                        this.showAlert(xInvoicesApproved, 'success');
                    })
                    .catch(error => {
                        this.isLoading = true;
                        console.log('error');
                    });
                }else{
                this.showAlert(this.$t('form.invoice.atleastoneselected'), 'danger');
                }
            },
            sendEmail(){
                if(this.selectedInvoices.length>0){
                this.isLoading = true;
                this.showAlert(this.$t('form.invoice.waittonotify'), 'primary');
                axios
                    .post('/api/students/invoices/notifications',{
                        selectedInvoices:this.selectedInvoices
                    })
                    .then(response => {
                        this.isLoading = false;
                        location.reload();
                    })
                    .catch(error => {
                        this.isLoading = true;
                        console.log('error');
                    });
                }else{
                this.showAlert(this.$t('form.invoice.atleastoneselected'), 'danger');
                }
                
            },
            async getInitialData() {
                await axios
                    .get('/api/invoices/get-initial-data')
                    .then(response => {
                        this.invoicePurposes = response.data.invoice_purposes;
                        this.currencies = response.data.currencies;
                    });
            },

            async getInvoices() {

                let source = axios.CancelToken.source();

                if (this.request_source != "")
                    this.request_source.cancel("Operation canceled by the user.");

                let filters = await this.buildQueryString(this.search);

                this.isLoading = true;
                this.request_source = source;
                axios
                    .get(`/api/invoices?pending=${this.pending}&${filters}`, {
                        cancelToken: this.request_source.token
                    })
                    .then(response => {
                        this.isLoading = false;
                        this.invoices = response.data.data.data;
                        this.pagination = response.data.pagination;
                    })
                    .catch(error => {
                        this.isLoading = false;
                        console.log(error);
                    });
            },

            paginate() {
                this.getInvoices();
            },

            goToShow(id) {
                if (!this.permissions.viewInvoices) return;

                this.$router.push({
                    name: "InvoiceShow",

                    params: {
                        id: id
                    }
                });
            },

            showAlert(msg, cls = 'success') {
                this.alertMessage = msg;
                this.alertClass = cls;
                this.alert = true;
            }
        }
    }

</script>
